import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Button, Card, Collapse, Divider, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { slugifyHeading } from '../../../components/markdown/Markdown';
// hooks
import useResponsive from '../../../hooks/useResponsive';

// ----------------------------------------------------------------------

// Below this number of sections a table of contents adds friction instead of
// helping, so the block hides itself on short posts.
const MIN_ITEMS = 3;

// Anchor targets need to clear the fixed site header.
const SCROLL_OFFSET = 160;

const HEADING_PATTERN = /^(#{2,3})\s+(.+?)\s*#*$/gm;

// A rendered `<h2>`/`<h3>` without an id — the Quill HTML case. The tag name
// gives the level; nested markup is stripped to the heading text.
const HTML_HEADING_PATTERN = /<(h[23])[^>]*>([\s\S]*?)<\/h[23]>/gi;

const stripHtml = (value) =>
  String(value ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/&[a-z0-9#]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Recursive rather than iterative so the heading parser stays compatible with
// the airbnb lint rule that forbids loops.
const makeUniqueId = (base, used, suffix = 1) => {
  const candidate = suffix === 1 ? base : `${base}-${suffix}`;

  return used.has(candidate) ? makeUniqueId(base, used, suffix + 1) : candidate;
};

const parseHeadings = (body) => {
  const used = new Set();
  const source = body || '';

  const pushHeading = (items, text, level) => {
    const clean = stripHtml(text).trim();

    if (!clean) {
      return items;
    }

    const base = slugifyHeading(clean);

    if (!base) {
      return items;
    }

    const id = makeUniqueId(base, used);
    used.add(id);

    return [...items, { id, text: clean, level }];
  };

  // Quill HTML bodies carry real heading tags; markdown fallback rows carry
  // `##` lines. Parse whichever representation the body uses.
  if (/<h[23][\s>]/i.test(source)) {
    return [...source.matchAll(HTML_HEADING_PATTERN)].reduce(
      (items, match) => pushHeading(items, match[2], Number(match[1].slice(1))),
      []
    );
  }

  return [...source.matchAll(HEADING_PATTERN)].reduce((items, match) => pushHeading(items, match[2], match[1].length), []);
};

// Assign the parsed anchors to the live heading elements inside the article
// ref. Quill HTML ships without ids, so without this the TOC links would
// point nowhere; the markdown renderer already id's its own headings, in
// which case this is a harmless no-op.
export function syncArticleHeadingIds(articleNode, items) {
  if (!articleNode || !Array.isArray(items) || items.length === 0) {
    return;
  }

  const headings = articleNode.querySelectorAll('h2, h3');

  items.forEach((item, index) => {
    const node = headings[index];

    if (node && !node.id) {
      node.id = item.id;
    }
  });
};

// ----------------------------------------------------------------------

// "In this article" navigation with scroll-spy.
// `sidebar` renders the sticky desktop rail; `inline` renders a collapsible
// card that sits above the article on small screens.
//
// The detail body is Quill HTML from the backend (markdown on fallback rows),
// so headings are parsed from rendered `<h2>/<h3>` tags as well as markdown
// `##` lines; see `syncArticleHeadingIds` for how the anchors land on the
// live article headings.
export default function BlogTableOfContents({ content, variant = 'sidebar' }) {
  const items = useMemo(() => parseHeadings(content), [content]);
  const [activeId, setActiveId] = useState(items[0]?.id ?? '');
  const [open, setOpen] = useState(false);

  const isDesktop = useResponsive('up', 'md');
  const isInline = variant === 'inline';
  const isActiveLayout = isInline ? !isDesktop : isDesktop;

  useEffect(() => {
    setActiveId(items[0]?.id ?? '');
  }, [items]);

  useEffect(() => {
    if (items.length === 0 || !isActiveLayout) {
      return undefined;
    }

    let frame = null;

    const update = () => {
      frame = null;

      // The active section is the last heading that has already passed under
      // the sticky header, which is what the reader is currently looking at.
      const passed = items
        .map((item) => ({ id: item.id, node: document.getElementById(item.id) }))
        .filter((entry) => entry.node && entry.node.getBoundingClientRect().top - SCROLL_OFFSET <= 0);

      setActiveId(passed.length > 0 ? passed[passed.length - 1].id : items[0].id);
    };

    const onScroll = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(update);
      }
    };

    update();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [items, isActiveLayout]);

  if (items.length < MIN_ITEMS) {
    return null;
  }

  const handleClick = (event, id) => {
    const node = document.getElementById(id);

    if (!node) {
      return;
    }

    event.preventDefault();
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', `#${id}`);

    if (isInline) {
      setOpen(false);
    }
  };

  const list = (
    <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
      {items.map((item) => {
        const isActive = item.id === activeId;

        return (
          <Box component="li" key={item.id} sx={{ py: 0.5, pl: item.level === 3 ? 2 : 0 }}>
            <Box
              component="a"
              href={`#${item.id}`}
              onClick={(event) => handleClick(event, item.id)}
              sx={{
                display: 'block',
                py: 0.5,
                fontSize: 13.5,
                lineHeight: 1.5,
                textDecoration: 'none',
                borderLeft: '2px solid',
                borderColor: isActive ? 'primary.main' : 'transparent',
                pl: 1.5,
                color: isActive ? 'primary.main' : 'text.secondary',
                fontWeight: isActive ? 700 : 400,
                transition: 'color 0.2s ease, border-color 0.2s ease',
                '@media (hover: hover)': {
                  '&:hover': { color: 'primary.main' },
                },
              }}
            >
              {item.text}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const footerNote = (
    <Typography
      variant="caption"
      sx={{
        mt: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        color: (theme) => alpha(theme.palette.text.disabled, 0.9),
      }}
    >
      <Iconify icon="eva:arrow-upward-outline" sx={{ width: 14, height: 14 }} />
      {items.length} sections
    </Typography>
  );

  if (isInline) {
    return (
      <Card sx={{ p: 2 }}>
        <Button
          fullWidth
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls="blog-toc-list"
          endIcon={
            <Iconify
              icon="eva:chevron-down-outline"
              sx={{
                width: 20,
                height: 20,
                transition: 'transform 0.25s ease',
                transform: open ? 'rotate(180deg)' : 'none',
              }}
            />
          }
          sx={{
            justifyContent: 'space-between',
            px: 0,
            color: 'text.primary',
            '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
            In this article
          </Typography>
        </Button>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 1.5 }} />
          <Box id="blog-toc-list">{list}</Box>
        </Collapse>
      </Card>
    );
  }

  return (
    <Box
      component="nav"
      aria-label="Table of contents"
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 1.5 }}>
        In this article
      </Typography>

      <Divider sx={{ mb: 1.5 }} />

      {list}

      {footerNote}
    </Box>
  );
}

BlogTableOfContents.propTypes = {
  content: PropTypes.string,
  variant: PropTypes.oneOf(['sidebar', 'inline']),
};